import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

const TEAL = "#22c55e";
const YOUTUBE_EMBED_URL = "https://www.youtube.com/embed/e5OYzMTbotw?rel=0";
const DOWNLOAD_FILENAME = "THE-CAGE-The-Biology-Trader.pdf";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Non-blocking, fire-and-forget tracking. Never touches the download navigation.
const trackDownload = () => {
  const email = sessionStorage.getItem("bt_lead_email");
  if (!email) return;
  try {
    const url = `${API}/free-book/downloaded`;
    const payload = JSON.stringify({ email, via: "thankyou" });
    const blob = new Blob([payload], { type: "application/json" });
    if (navigator.sendBeacon && navigator.sendBeacon(url, blob)) return;
    fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: payload,
      keepalive: true,
      mode: "cors",
      credentials: "omit",
    }).catch(() => {});
  } catch { /* tracking never blocks the download */ }
};

export default function ThankYou() {
  // null = resolving, "" = no token (direct visit / lost state), otherwise the lead's token URL.
  const [downloadHref, setDownloadHref] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    let token = "";
    try {
      token = sessionStorage.getItem("bt_download_token") || "";
    } catch { /* sessionStorage may be unavailable */ }
    setDownloadHref(token ? `${API}/download/${token}/${DOWNLOAD_FILENAME}?via=thankyou` : "");
  }, []);

  return (
    <div className="min-h-screen bg-white text-neutral-950 flex flex-col" style={{ fontFamily: "'Inter Tight', ui-sans-serif, system-ui, sans-serif" }} data-testid="page-thank-you">
      <main className="flex-1">
        <section className="max-w-screen-md mx-auto px-6 md:px-12 pt-16 md:pt-28 pb-16 md:pb-24">
          <p className="text-xs font-semibold tracking-[0.25em] uppercase" style={{ color: TEAL }}>
            The Free Book
          </p>
          <h1 className="mt-6 text-4xl sm:text-5xl font-extrabold leading-[1.05] tracking-tight" data-testid="thank-you-headline">
            Here&rsquo;s your book.
          </h1>

          <div className="mt-12 md:mt-16" data-testid="thank-you-download">
            {downloadHref ? (
              <a
                href={downloadHref}
                download={DOWNLOAD_FILENAME}
                type="application/pdf"
                onClick={trackDownload}
                className="inline-flex items-center justify-center w-full sm:w-auto text-base sm:text-lg font-extrabold tracking-tight px-8 py-5 bg-neutral-950 text-white hover:bg-neutral-800 transition-colors"
                data-testid="thank-you-download-button"
              >
                DOWNLOAD THE BOOK (PDF)
              </a>
            ) : downloadHref === "" ? (
              <p
                className="text-base sm:text-lg font-semibold text-neutral-950"
                data-testid="thank-you-email-line"
              >
                Your download link is no longer active.
              </p>
            ) : (
              <span
                role="button"
                aria-disabled="true"
                aria-busy="true"
                className="inline-flex items-center justify-center w-full sm:w-auto text-base sm:text-lg font-extrabold tracking-tight px-8 py-5 bg-neutral-800 text-white/70 cursor-wait select-none"
                data-testid="thank-you-download-button-loading"
              >
                <svg
                  className="animate-spin -ml-1 mr-3 h-4 w-4 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                </svg>
                PREPARING YOUR DOWNLOAD…
              </span>
            )}
            <p className="mt-4 text-sm text-neutral-500" data-testid="thank-you-download-note">
              {downloadHref === ""
                ? "Request the book again using the form and a new link will be sent to your inbox."
                : "A copy is also on its way to your inbox, so you can re-download it anytime."}
            </p>
            {downloadHref !== "" && (
              <p className="mt-1 text-sm text-neutral-500" data-testid="thank-you-promotions-note">
                If it lands in Promotions, drag it to Primary.
              </p>
            )}
          </div>

          <div className="mt-16 md:mt-24 border-t border-neutral-200 pt-12" data-testid="thank-you-video-section">
            <h2 className="text-2xl md:text-3xl font-bold tracking-tight">
              Watch my latest YouTube video.
            </h2>
            <div className="mt-8 aspect-video w-full bg-neutral-100" data-testid="thank-you-video-placeholder">
              {YOUTUBE_EMBED_URL ? (
                <iframe
                  className="w-full h-full"
                  src={YOUTUBE_EMBED_URL}
                  title="The Biology Trader — latest video"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <span className="text-sm tracking-[0.2em] uppercase text-neutral-400">Video coming soon</span>
                </div>
              )}
            </div>
            <Link
              to="/"
              className="mt-6 inline-block text-sm text-neutral-500 hover:text-neutral-950 underline underline-offset-4"
              data-testid="thank-you-site-link"
            >
              More at thebiologytrader.com →
            </Link>
          </div>
        </section>
      </main>

      <footer className="border-t border-neutral-200" data-testid="thank-you-footer">
        <div className="max-w-screen-md mx-auto px-6 md:px-12 py-10 text-sm text-neutral-500">
          <div className="flex items-center justify-between">
            <span>© {new Date().getFullYear()} Biology Trader Education LLC</span>
            <span className="flex items-center gap-5">
              <Link to="/risk-disclosure" className="hover:text-neutral-950 underline underline-offset-4" data-testid="thank-you-risk-link">
                Risk Disclosure
              </Link>
              <Link to="/privacy" className="hover:text-neutral-950 underline underline-offset-4" data-testid="thank-you-privacy-link">
                Privacy Policy
              </Link>
            </span>
          </div>
          <p className="block mt-4 text-xs text-neutral-400" data-testid="thank-you-risk-line">
            Trading involves substantial risk. Past performance does not guarantee future results.
          </p>
        </div>
      </footer>
    </div>
  );
}
